import { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import type {
  ManutencaoDto,
  ManutencaoPrioridade,
  ManutencaoTipo,
  MaquinarioDto,
} from "@/services/locacoes";
import {
  extractApiError,
  MANUTENCAO_PRIORIDADE_LABELS,
  MANUTENCAO_TIPO_LABELS,
} from "@/services/locacoes";
import { resolveDefaultMaquinaId } from "./locacoesUtils";
import { upperLocacaoText } from "./upperLocacaoText";

interface Props {
  open: boolean;
  editing?: ManutencaoDto | null;
  maquinarios: MaquinarioDto[];
  usuarios: Array<{ id: string; displayName: string }>;
  /** Pré-seleciona máquina (ex.: abertura a partir da Garagem). */
  presetMaquinaId?: string | null;
  busy?: boolean;
  onClose: () => void;
  onSave: (values: {
    maquinaId: string;
    tipo: ManutencaoTipo;
    descricao: string;
    dataAbertura: string;
    horimetroAtual: number;
    prioridade: ManutencaoPrioridade;
    responsavelId: string;
    responsavelNome: string;
    dataPrevista: string;
    dataConclusao?: string | null;
    custoEstimado?: number;
  }) => Promise<void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

const TIPOS: ManutencaoTipo[] = ["preventiva", "corretiva", "revisao", "troca_oleo"];
const PRIORIDADES: ManutencaoPrioridade[] = ["baixa", "media", "alta"];

export function ModalManutencao({
  open,
  editing,
  maquinarios,
  usuarios,
  presetMaquinaId,
  busy,
  onClose,
  onSave,
  onDelete,
  onMutationComplete,
}: Props) {
  const [maquinaId, setMaquinaId] = useState("");
  const [tipo, setTipo] = useState<ManutencaoTipo>("preventiva");
  const [descricao, setDescricao] = useState("");
  const [dataAbertura, setDataAbertura] = useState(new Date().toISOString().slice(0, 10));
  const [horimetroAtual, setHorimetroAtual] = useState("");
  const [prioridade, setPrioridade] = useState<ManutencaoPrioridade>("media");
  const [responsavelId, setResponsavelId] = useState("");
  const [dataPrevista, setDataPrevista] = useState("");
  const [dataConclusao, setDataConclusao] = useState("");
  const [valorManutencao, setValorManutencao] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selected = maquinarios.find((m) => m.id === maquinaId);
  const elegiveis = maquinarios.filter((m) => m.status !== "inativo");

  const formSessionKey = `${editing?.id ?? "new"}::${presetMaquinaId ?? ""}`;

  useModalFormSession(open, formSessionKey, () => {
    setError(null);
    if (editing) {
      setMaquinaId(editing.maquinaId);
      setTipo(editing.tipo);
      setDescricao(upperLocacaoText(editing.descricao));
      setDataAbertura(editing.dataAbertura.slice(0, 10));
      setHorimetroAtual(String(editing.horimetroAtual));
      setPrioridade(editing.prioridade);
      setResponsavelId(editing.responsavelId);
      setDataPrevista(editing.dataPrevista?.slice(0, 10) ?? "");
      setDataConclusao(editing.dataConclusao?.slice(0, 10) ?? "");
      setValorManutencao(
        editing.custoEstimado != null && editing.custoEstimado > 0 ? String(editing.custoEstimado) : "",
      );
      return;
    }
    const ativas = maquinarios.filter((m) => m.status !== "inativo");
    const preset = resolveDefaultMaquinaId(ativas.length > 0 ? ativas : maquinarios, presetMaquinaId);
    setMaquinaId(preset);
    setTipo("preventiva");
    setDescricao("");
    setDataAbertura(new Date().toISOString().slice(0, 10));
    const presetMaq = maquinarios.find((m) => m.id === preset);
    setHorimetroAtual(presetMaq ? String(presetMaq.horimetroAtual) : "");
    setPrioridade("media");
    setResponsavelId("");
    setDataPrevista("");
    setDataConclusao("");
    setValorManutencao("");
  });

  useEffect(() => {
    if (open && selected && !editing) setHorimetroAtual(String(selected.horimetroAtual));
  }, [open, editing?.id, selected?.id]);

  const handleSubmit = async () => {
    setError(null);
    const resp = usuarios.find((u) => u.id === responsavelId);
    if (!editing && !maquinaId) {
      setError("Selecione o maquinário.");
      return;
    }
    if (!descricao.trim()) {
      setError("Informe a descrição da manutenção.");
      return;
    }
    if (!responsavelId) {
      setError("Selecione o responsável.");
      return;
    }
    try {
      const valorTrim = valorManutencao.trim().replace(",", ".");
      const custoEstimado =
        valorTrim !== "" && Number.isFinite(Number(valorTrim)) ? Math.max(0, Number(valorTrim)) : undefined;
      await onSave({
        maquinaId: editing?.maquinaId ?? maquinaId,
        tipo,
        descricao,
        dataAbertura,
        horimetroAtual: Number(horimetroAtual) || selected?.horimetroAtual || editing?.horimetroAtual || 0,
        prioridade,
        responsavelId,
        responsavelNome: resp?.displayName ?? editing?.responsavelNome ?? "",
        dataPrevista,
        dataConclusao: editing?.status === "finalizada" ? dataConclusao || null : undefined,
        ...(custoEstimado !== undefined ? { custoEstimado } : editing ? { custoEstimado: 0 } : {}),
      });
      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      setError(extractApiError(e, editing ? "Erro ao salvar manutenção." : "Erro ao abrir manutenção."));
    }
  };

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
        <Wrench className="h-4 w-4 text-amber-600" />
      </div>
      <span className="font-bold text-slate-900">{editing ? "Editar manutenção" : "Nova manutenção"}</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button type="button" variant="secondary" onClick={onDelete} disabled={busy}>
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} loading={busy}>
              {editing ? "Salvar" : "Abrir manutenção"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
        )}
        {editing ? (
          <Input label="Maquinário" value={editing.maquinaNome} disabled />
        ) : (
          <Select
            label="Maquinário *"
            value={maquinaId}
            onChange={(e) => setMaquinaId(e.target.value)}
            options={elegiveis.map((m) => ({ value: m.id, label: m.nome }))}
            placeholder="Selecione…"
          />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as ManutencaoTipo)}
            options={TIPOS.map((t) => ({ value: t, label: MANUTENCAO_TIPO_LABELS[t] }))}
          />
          <Select
            label="Prioridade"
            value={prioridade}
            onChange={(e) => setPrioridade(e.target.value as ManutencaoPrioridade)}
            options={PRIORIDADES.map((p) => ({ value: p, label: MANUTENCAO_PRIORIDADE_LABELS[p] }))}
          />
        </div>
        <Input label="Horímetro atual" type="number" value={horimetroAtual} onChange={(e) => setHorimetroAtual(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Data abertura" type="date" value={dataAbertura} onChange={(e) => setDataAbertura(e.target.value)} />
          <Input label="Data prevista" type="date" value={dataPrevista} onChange={(e) => setDataPrevista(e.target.value)} />
        </div>
        {editing?.status === "finalizada" && (
          <Input label="Data conclusão" type="date" value={dataConclusao} onChange={(e) => setDataConclusao(e.target.value)} />
        )}
        <Select
          label="Responsável"
          value={responsavelId}
          onChange={(e) => setResponsavelId(e.target.value)}
          options={[
            { value: "", label: "Selecione…" },
            ...usuarios.map((u) => ({ value: u.id, label: u.displayName })),
          ]}
        />
        <Input
          label="Valor da manutenção (R$)"
          type="number"
          min={0}
          step="0.01"
          value={valorManutencao}
          onChange={(e) => setValorManutencao(e.target.value)}
          placeholder="Opcional"
        />
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Descrição *</label>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(upperLocacaoText(e.target.value))}
            rows={3}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}
